import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { trackPageView, trackFAQInteraction, trackCTAClick, trackInternalLink } from '../utils/analytics';

const content = {
  en: {
    // SEO Metadata
    title: 'Where to Get Ulthera Prime Treatment in Richmond Hill? | Halo Aesthetic Clinic',
    description: 'Looking for Ulthera Prime treatment in Richmond Hill? Halo Aesthetic Clinic offers advanced Ulthera Prime non-invasive skin tightening. Book your consultation today.',
    
    // Main Question (H1)
    mainQuestion: 'Where to Get Ulthera Prime Treatment in Richmond Hill?',
    
    // Supporting Sections (H2)
    intro: 'What is Ulthera Prime?',
    introText: 'Ulthera Prime is the latest generation of Ultherapy, a non-invasive treatment that uses focused ultrasound energy to lift and tighten the skin naturally. FDA-approved with visible results and no downtime.',
    
    benefitsTitle: 'Benefits of Ulthera Prime',
    benefitsText: 'No downtime, natural-looking results, and long-lasting effects that continue to improve over time.',
    
    treatmentTitle: 'What to Expect',
    treatmentText: 'Our experienced practitioners will guide you through the process, which typically takes 60-90 minutes. The procedure uses focused ultrasound to stimulate collagen production deep within your skin.',
    
    locationTitle: 'Why Choose Us',
    locationText: 'Located conveniently in Richmond Hill, our certified practitioners have extensive experience with this advanced technology.',
    
    bookingTitle: 'Book Your Consultation',
    bookingText: 'Ready to experience the benefits? Schedule a consultation to learn how this treatment can help you achieve your aesthetic goals.',
    
    ctaButton: 'Book Consultation',
    
    // FAQ Placeholder
    faqTitle: 'Frequently Asked Questions',
    faqPlaceholder: 'FAQ content will be added here with common questions about Ulthera Prime treatment, procedure details, expected results, recovery time, and pricing in Richmond Hill.',
  },
  cn: {
    // SEO Metadata
    title: 'Richmond Hill哪里可以做Ulthera Prime治疗？| Halo医美中心',
    description: '寻找Richmond Hill的Ulthera Prime治疗？Halo医美中心提供先进的Ulthera Prime非侵入性皮肤紧致治疗。立即预约咨询。',
    
    // Main Question (H1)
    mainQuestion: 'Richmond Hill哪里可以做Ulthera Prime治疗？',
    
    // Supporting Sections (H2)
    intro: '什么是Ulthera Prime？',
    introText: 'Ulthera Prime是Ultherapy的最新一代，这是一种非侵入性治疗，使用聚焦超声波能量自然提升和紧致皮肤。FDA批准，可见效果，无需恢复期。',
    
    benefitsTitle: 'Ulthera Prime的优势',
    benefitsText: '无需恢复期、自然效果以及随时间持续改善的持久效果。',
    
    treatmentTitle: '治疗流程',
    treatmentText: '我们经验丰富的医师将指导您完成整个过程，通常需要60-90分钟。该程序使用聚焦超声波刺激皮肤深处的胶原蛋白生成。',
    
    locationTitle: '为什么选择我们',
    locationText: '位于Richmond Hill的便利位置，我们经过认证的医师团队在使用这种先进技术方面拥有丰富经验。',
    
    bookingTitle: '预约咨询',
    bookingText: '准备好体验优势了吗？预约咨询，了解这种治疗如何帮助您实现医美目标。',
    
    ctaButton: '预约咨询',
    
    // FAQ Placeholder
    faqTitle: '常见问题',
    faqPlaceholder: '常见问题内容将在此添加，包括关于Ulthera Prime治疗、程序详情、预期效果、恢复时间和Richmond Hill定价的常见问题。',
  }
};

const UltheraRichmondHill = () => {
  const { lang } = useLang();
  const c = content[lang];
  const location = useLocation();
  const hasTrackedPageView = useRef(false);

  // 跟踪页面浏览和核心问题展示
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPageView(location.pathname, c.title, c.mainQuestion);
      hasTrackedPageView.current = true;
    }
  }, [location.pathname, c.title, c.mainQuestion]);

  // FAQ交互追踪处理函数
  const handleFAQToggle = (question, isOpen) => {
    trackFAQInteraction(question, isOpen ? 'expand' : 'collapse', location.pathname);
  };

  // CTA点击追踪处理函数
  const handleCTAClick = () => {
    trackCTAClick(c.ctaButton, 'hero', location.pathname);
  };

  // 内部链接点击追踪处理函数
  const handleInternalLinkClick = (linkText, linkPath) => {
    trackInternalLink(linkText, linkPath, location.pathname);
  };

  // JSON-LD Structured Data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.haloaestheticclinic.com/ulthera-richmond-hill#webpage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": lang === 'en'
          ? "Where can I get Ulthera Prime treatment in Richmond Hill?"
          : "Richmond Hill哪里可以做Ulthera Prime治疗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Halo Aesthetic Clinic in Richmond Hill offers advanced Ulthera Prime non-invasive skin tightening. This FDA-approved treatment provides natural-looking results with no downtime."
            : "Halo医美中心在Richmond Hill提供先进的Ulthera Prime非侵入性皮肤紧致治疗。这种FDA批准的治疗提供自然效果，无需恢复期。"
        }
      },
      {
        "@type": "Question",
        "name": lang === 'en'
          ? "What is the difference between Ulthera Prime and traditional facelifts?"
          : "Ulthera Prime和传统拉皮有什么区别？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Ulthera Prime is non-invasive, requires no surgery or downtime, and uses focused ultrasound to stimulate natural collagen production. Traditional facelifts are surgical procedures with longer recovery. Ulthera Prime provides gradual, natural-looking results."
            : "Ulthera Prime是非侵入性的，无需手术或恢复期，使用聚焦超声波刺激天然胶原蛋白生成。传统拉皮是手术程序，恢复期更长。Ulthera Prime提供逐渐的自然效果。"
        }
      },
      {
        "@type": "Question",
        "name": lang === 'en'
          ? "How long do Ulthera Prime results last?"
          : "Ulthera Prime效果能持续多久？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": lang === 'en'
            ? "Ulthera Prime results can improve for up to 6 months and can last for over a year. Results continue to improve as your body produces new collagen over time."
            : "Ulthera Prime效果可以改善长达6个月，可持续一年以上。随着身体随时间产生新的胶原蛋白，效果持续改善。"
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": "https://www.haloaestheticclinic.com/#organization",
    "name": "Halo Aesthetic Clinic",
    "url": "https://www.haloaestheticclinic.com",
    "image": "https://www.haloaestheticclinic.com/logo.jpg",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.haloaestheticclinic.com/logo.jpg"
    },
    "telephone": "+14377555409",
    "email": "info@haloaestheticclinic.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1595 16th Ave Suite 103",
      "addressLocality": "Richmond Hill",
      "addressRegion": "ON",
      "postalCode": "L4B 3N9",
      "addressCountry": "CA"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Tuesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Wednesday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Thursday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Friday",
        "opens": "13:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "11:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "19:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "medicalSpecialty": "Dermatology"
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": c.mainQuestion,
    "description": c.description,
    "author": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "publisher": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.haloaestheticclinic.com/ulthera-richmond-hill"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": lang === 'en' ? "Ulthera Prime Treatment" : "Ulthera Prime治疗",
    "serviceType": "Ulthera Prime",
    "description": lang === 'en'
      ? "Advanced non-invasive skin tightening treatment using focused ultrasound energy. FDA-approved Ulthera Prime provides natural-looking results with no downtime. Available in Richmond Hill."
      : "使用聚焦超声波能量的先进非侵入性皮肤紧致治疗。FDA批准的Ulthera Prime提供自然效果，无需恢复期。在Richmond Hill提供。",
    "url": "https://www.haloaestheticclinic.com/ulthera-richmond-hill",
    "provider": {
      "@id": "https://www.haloaestheticclinic.com/#organization"
    },
    "areaServed": {
      "@type": "City",
      "name": "Richmond Hill"
    },
    "category": "Medical Aesthetic Treatment",
    "brand": {
      "@type": "Brand",
      "name": "Ulthera Prime"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.haloaestheticclinic.com/ulthera-richmond-hill",
      "servicePhone": "+14377555409"
    }
  };

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
        <meta name="description" content={c.description} />
        <link rel="canonical" href="https://www.haloaestheticclinic.com/ulthera-richmond-hill" />
        <meta name="keywords" content={lang === 'en'
          ? 'Ulthera Prime Richmond Hill, Ultherapy Richmond Hill, non-invasive skin tightening, ultrasound skin lifting, Richmond Hill aesthetic clinic'
          : 'Ulthera Prime Richmond Hill, Ultherapy Richmond Hill, 非侵入性皮肤紧致, 超声波皮肤提升, Richmond Hill医美诊所'
        } />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>
      
      <div className="bg-softGradient bg-no-repeat bg-top text-text">
        {/* Hero Section with Main Question */}
        <section className="py-16 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {c.mainQuestion}
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {c.introText}
            </p>
          </div>
        </section>

        {/* Supporting Section 1: Benefits */}
        <section className="py-16 bg-white/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 text-center">{c.benefitsTitle}</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              {c.benefitsText}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'No Downtime' : '无需恢复期'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Return to your daily activities immediately after treatment with no recovery period needed.'
                    : '治疗后立即恢复日常活动，无需恢复期。'
                  }
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Natural Results' : '自然效果'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Gradual, natural-looking improvement as your body produces new collagen over time.'
                    : '随着身体随时间产生新的胶原蛋白，逐渐自然改善。'
                  }
                </p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40">
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {lang === 'en' ? 'Long-Lasting Effects' : '持久效果'}
                </h3>
                <p className="text-gray-700">
                  {lang === 'en'
                    ? 'Results continue to improve for up to 6 months and can last for over a year.'
                    : '效果持续改善长达6个月，可持续一年以上。'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Supporting Section 2: Treatment Process & Booking */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">{c.treatmentTitle}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {c.treatmentText}
              </p>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 mt-8">{c.locationTitle}</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {c.locationText}
              </p>
              <Link
                to="/book-form"
                onClick={handleCTAClick}
                className="inline-block px-8 py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60 text-lg font-semibold"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
                }}
                aria-label={lang === 'en' ? 'Book your Ulthera Prime consultation in Richmond Hill' : '在Richmond Hill预约Ulthera Prime咨询'}
              >
                {c.ctaButton}
              </Link>
            </div>
            
            {/* Related Services Section */}
            <div className="mt-16 bg-white/70 backdrop-blur-sm rounded-xl p-8 border border-champagne/40">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">
                {lang === 'en' ? 'Explore More Services' : '探索更多服务'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link 
                  to="/after-hours-services"
                  onClick={() => handleInternalLinkClick(
                    lang === 'en' ? 'Friday After-Hours Services' : '周五延长服务时间',
                    '/after-hours-services'
                  )}
                  className="block p-6 rounded-lg border border-champagne/40 hover:border-[#2E6B4C] hover:shadow-glow transition-all bg-white/50"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {lang === 'en' ? 'Friday After-Hours Services' : '周五延长服务时间'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lang === 'en' 
                      ? 'Convenient Friday evening appointments from 1PM to 9PM.'
                      : '方便的周五晚上预约，从下午1点到晚上9点。'
                    }
                  </p>
                </Link>
                <Link 
                  to="/asian-skin-treatment"
                  onClick={() => handleInternalLinkClick(
                    lang === 'en' ? 'Asian Skin Specialized Treatment' : '亚洲皮肤专业治疗',
                    '/asian-skin-treatment'
                  )}
                  className="block p-6 rounded-lg border border-champagne/40 hover:border-[#2E6B4C] hover:shadow-glow transition-all bg-white/50"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {lang === 'en' ? 'Asian Skin Specialized Treatment' : '亚洲皮肤专业治疗'}
                  </h4>
                  <p className="text-gray-700 text-sm">
                    {lang === 'en' 
                      ? 'Specialized treatments designed for Asian skin types.'
                      : '专为亚洲肤质设计的专业治疗。'
                    }
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-softRadial">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">{c.faqTitle}</h2>
            <div className="space-y-4">
              <details 
                className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                onToggle={(e) => {
                  const question = lang === 'en' ? 'How long do Ulthera Prime results last?' : 'Ulthera Prime效果能持续多久？';
                  handleFAQToggle(question, e.target.open);
                }}
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                  <span className="flex items-center justify-between">
                    <span>{lang === 'en' ? 'How long do Ulthera Prime results last?' : 'Ulthera Prime效果能持续多久？'}</span>
                    <svg className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                  <p>
                    {lang === 'en' 
                      ? 'Ulthera Prime results can improve for up to 6 months and can last for over a year. Results continue to improve as your body produces new collagen over time. Many patients see optimal results around 3-6 months after treatment.'
                      : 'Ulthera Prime效果可以改善长达6个月，可持续一年以上。随着身体随时间产生新的胶原蛋白，效果持续改善。许多患者在治疗后3-6个月看到最佳效果。'
                    }
                  </p>
                </div>
              </details>
              
              <details 
                className="bg-white/70 backdrop-blur-sm rounded-xl border border-champagne/40 overflow-hidden transition-all duration-300 hover:shadow-glow"
                onToggle={(e) => {
                  const question = lang === 'en' ? 'Is there any downtime after Ulthera Prime treatment?' : 'Ulthera Prime治疗后需要恢复期吗？';
                  handleFAQToggle(question, e.target.open);
                }}
              >
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 text-lg hover:text-[#2E6B4C] transition-colors list-none">
                  <span className="flex items-center justify-between">
                    <span>{lang === 'en' ? 'Is there any downtime after Ulthera Prime treatment?' : 'Ulthera Prime治疗后需要恢复期吗？'}</span>
                    <svg className="w-5 h-5 text-[#2E6B4C] transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-4 pt-2 text-gray-700 leading-relaxed">
                  <p>
                    {lang === 'en' 
                      ? 'Ulthera Prime requires no downtime, allowing you to return to your daily activities immediately after treatment. Some patients may experience mild redness or slight swelling, which typically subsides within a few hours.'
                      : 'Ulthera Prime无需恢复期，治疗后可以立即恢复日常活动。一些患者可能会出现轻微红肿，通常在几小时内消退。'
                    }
                  </p>
                </div>
              </details>
              
              <div className="text-center mt-6">
                <Link 
                  to="/faq" 
                  className="text-[#2E6B4C] font-semibold hover:underline"
                  aria-label={lang === 'en' ? 'View all frequently asked questions' : '查看所有常见问题'}
                >
                  {lang === 'en' ? 'View All FAQs →' : '查看所有常见问题 →'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default UltheraRichmondHill;

